import { Loader2, Check } from "lucide-react";
import { Modal, Button } from "@repo/core-ui";

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEditMode: boolean;
  loading: boolean;
  formData: any;
  setFormData: (data: any) => void;
  handleSave: () => void;
}

export const SubscriptionModal = ({
  isOpen,
  onClose,
  isEditMode,
  loading,
  formData,
  setFormData,
  handleSave,
}: SubscriptionModalProps) => {
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={
        isEditMode ? "Edit Subscription Plan" : "Add New Subscription Plan"
      }
      maxWidth="max-w-2xl"
      maxHeight="max-h-[90vh]"
      footer={
        <div className="flex justify-end items-center w-full gap-3">
          <Button
            onClick={onClose}
            className="bg-[#111c44] text-white hover:bg-black dark:hover:bg-[#1b254b] border-none px-6 rounded-xl shadow-lg shadow-black/20"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 border-none px-6 rounded-xl flex items-center gap-2 shadow-sm"
            disabled={loading}
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Check size={16} />
            )}
            {isEditMode ? "Save Changes" : "Create Plan"}
          </Button>
        </div>
      }
    >
      <div className="space-y-6 pt-2 pb-4 px-1">
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 -mt-4">
          {isEditMode
            ? "Update the details of the selected subscription plan."
            : "Create a new plan for properties."}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Plan Name */}
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Plan Name
            </label>
            <input
              type="text"
              placeholder="e.g. Premium Plan"
              value={formData.PlanName}
              onChange={(e) =>
                setFormData({ ...formData, PlanName: e.target.value })
              }
              className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 text-sm focus:bg-white dark:focus:bg-[#0b1437] focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 transition-all font-medium text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>

          {/* Price */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Price (₹)
            </label>
            <input
              type="number"
              value={formData.Price || ""}
              onChange={(e) =>
                setFormData({ ...formData, Price: Number(e.target.value) })
              }
              className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 text-sm focus:bg-white dark:focus:bg-[#0b1437] focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 transition-all font-medium text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Duration (Months)
            </label>
            <input
              type="number"
              value={formData.DurationMonths || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  DurationMonths: Number(e.target.value),
                })
              }
              disabled={formData.IsLifetime}
              className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 text-sm focus:bg-white dark:focus:bg-[#0b1437] focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 transition-all font-medium text-slate-700 dark:text-slate-200 disabled:opacity-50"
            />
          </div>

          {/* Max Users */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Max Users
            </label>
            <input
              type="number"
              value={formData.MaxUsers || ""}
              onChange={(e) =>
                setFormData({ ...formData, MaxUsers: Number(e.target.value) })
              }
              className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 text-sm focus:bg-white dark:focus:bg-[#0b1437] focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 transition-all font-medium text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>

          {/* Max Rooms */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Max Rooms
            </label>
            <input
              type="number"
              value={formData.MaxRooms || ""}
              onChange={(e) =>
                setFormData({ ...formData, MaxRooms: Number(e.target.value) })
              }
              className="w-full h-11 px-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 text-sm focus:bg-white dark:focus:bg-[#0b1437] focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 transition-all font-medium text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>

          {/* Description */}
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Description
            </label>
            <textarea
              rows={3}
              placeholder="Describe features"
              value={formData.Description}
              onChange={(e) =>
                setFormData({ ...formData, Description: e.target.value })
              }
              className="w-full p-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 text-sm focus:bg-white dark:focus:bg-[#0b1437] focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 transition-all font-medium text-slate-700 dark:text-slate-200 resize-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>

          {/* Toggles */}
          <div className="space-y-4 md:col-span-2 pt-2">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={formData.IsLifetime}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      IsLifetime: e.target.checked,
                    })
                  }
                />
                <div
                  className={`w-11 h-6 rounded-full transition-colors ${formData.IsLifetime ? "bg-blue-600 dark:bg-blue-500" : "bg-slate-200 dark:bg-[#1b254b]"}`}
                >
                  <div
                    className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.IsLifetime ? "translate-x-5" : "translate-x-0"}`}
                  />
                </div>
              </div>
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors">
                Lifetime Plan
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={formData.IsActive}
                  onChange={(e) =>
                    setFormData({ ...formData, IsActive: e.target.checked })
                  }
                />
                <div
                  className={`w-11 h-6 rounded-full transition-colors ${formData.IsActive ? "bg-emerald-500 dark:bg-emerald-600" : "bg-slate-200 dark:bg-[#1b254b]"}`}
                >
                  <div
                    className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.IsActive ? "translate-x-5" : "translate-x-0"}`}
                  />
                </div>
              </div>
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors">
                Active Status
              </span>
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
};
